import { useState } from 'react';

function americanToImplied(american) {
	const a = parseFloat(american);
	if (isNaN(a) || a === 0) return NaN;
	if (a > -100 && a < 100) return NaN;
	if (a > 0) return 100 / (a + 100);
	return Math.abs(a) / (Math.abs(a) + 100);
}

function impliedToAmerican(prob) {
	if (prob <= 0 || prob >= 1) return NaN;
	if (prob >= 0.5) return Math.round(-prob / (1 - prob) * 100);
	return Math.round((1 - prob) / prob * 100);
}

function devigMultiplicative(pA, pB) {
	const total = pA + pB;
	return { trueA: pA / total, trueB: pB / total };
}

function devigAdditive(pA, pB) {
	const vig = (pA + pB - 1) / 2;
	return { trueA: pA - vig, trueB: pB - vig };
}

function devigPower(pA, pB) {
	let lo = 0, hi = 2, mid;
	for (let i = 0; i < 100; i++) {
		mid = (lo + hi) / 2;
		const sum = Math.pow(pA, mid) + Math.pow(pB, mid);
		if (sum > 1) lo = mid;
		else hi = mid;
	}
	const k = (lo + hi) / 2;
	return { trueA: Math.pow(pA, k), trueB: Math.pow(pB, k) };
}

function devigShin(pA, pB) {
	const total = pA + pB;
	const z = (total - 1) / (total);
	const shinA = (Math.sqrt(z * z + 4 * (1 - z) * pA * pA / total) - z) / (2 * (1 - z));
	const shinB = (Math.sqrt(z * z + 4 * (1 - z) * pB * pB / total) - z) / (2 * (1 - z));
	const normTotal = shinA + shinB;
	return { trueA: shinA / normTotal, trueB: shinB / normTotal };
}

const METHODS = {
	multiplicative: { label: 'Multiplicative', fn: devigMultiplicative },
	additive: { label: 'Additive', fn: devigAdditive },
	power: { label: 'Power', fn: devigPower },
	shin: { label: 'Shin', fn: devigShin },
};

export const DevigCalculator = () => {
	const [oddsA, setOddsA] = useState('');
	const [oddsB, setOddsB] = useState('');
	const [method, setMethod] = useState('multiplicative');

	const pA = americanToImplied(oddsA);
	const pB = americanToImplied(oddsB);
	const hasResults = !isNaN(pA) && !isNaN(pB) && pA > 0 && pB > 0;

	let results = null;
	if (hasResults) {
		const { trueA, trueB } = METHODS[method].fn(pA, pB);
		const vigPct = ((pA + pB) - 1) * 100;
		const fairOddsA = impliedToAmerican(trueA);
		const fairOddsB = impliedToAmerican(trueB);
		results = { trueA, trueB, vigPct, fairOddsA, fairOddsB };
	}

	const handleReset = () => {
		setOddsA('');
		setOddsB('');
	};

	const formatAmerican = (odds) => {
		if (isNaN(odds)) return '—';
		return odds > 0 ? `+${odds}` : `${odds}`;
	};

	return (
		<div>
			<div className="calc-section">
				<h3 className="calc-section-title">Market Odds (American)</h3>
				<div className="calc-grid calc-grid-2">
					<div className="calc-input-group">
						<label className="calc-label">Side A Odds</label>
						<input
							className="calc-input"
							type="text"
							placeholder="-110"
							value={oddsA}
							onChange={(e) => setOddsA(e.target.value)}
						/>
					</div>
					<div className="calc-input-group">
						<label className="calc-label">Side B Odds</label>
						<input
							className="calc-input"
							type="text"
							placeholder="+105"
							value={oddsB}
							onChange={(e) => setOddsB(e.target.value)}
						/>
					</div>
				</div>
			</div>

			<div className="calc-section">
				<h3 className="calc-section-title">Devig Method</h3>
				<div className="calc-method-select">
					{Object.entries(METHODS).map(([key, { label }]) => (
						<button
							key={key}
							className={`calc-method-btn ${method === key ? 'active' : ''}`}
							onClick={() => setMethod(key)}
						>
							{label}
						</button>
					))}
				</div>
			</div>

			{results && (
				<div className="calc-results">
					<h4 className="calc-results-title">Devigged Results</h4>
					<div className="calc-result-row">
						<span className="calc-result-label">True Probability A</span>
						<span className="calc-result-value highlight">{(results.trueA * 100).toFixed(2)}%</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">True Probability B</span>
						<span className="calc-result-value highlight">{(results.trueB * 100).toFixed(2)}%</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Fair Odds A (No-Vig)</span>
						<span className="calc-result-value">{formatAmerican(results.fairOddsA)}</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Fair Odds B (No-Vig)</span>
						<span className="calc-result-value">{formatAmerican(results.fairOddsB)}</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Vig/Juice</span>
						<span className="calc-result-value negative">{results.vigPct.toFixed(2)}%</span>
					</div>
				</div>
			)}

			<button className="calc-reset-btn" onClick={handleReset}>Reset</button>
		</div>
	);
};
